import { Button, Card, ConfirmDialog, useToast } from '@citizen-walk/shared-ui';
import type {
  AiDialogueFactsConfig,
  AiDialogueFactsFieldSet,
  AiDialogueSettings,
  DistrictMode,
} from '@citizen-walk/shared-types';
import { useEffect, useState } from 'react';
import { collection, onSnapshot, type DocumentData } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { archiveMaster, upsertMaster, useAiDialogueSettings } from './useMasters';
import { MasterHistoryView } from './MasterHistoryView';
import { MasterImportExportButtons, RowExportButton } from './MasterImportExportButtons';
import { stripSystemFields } from './jsonImportExport';
import { ModeSelectField } from './components/ModeSelectField';
import {
  DIALOGUE_SCENARIO_TEMPLATES,
  STEP0_DESCRIPTION,
  STEP0_ID,
  STEP_ROLE_LABELS,
  TEMPLATE_VAR_LEGEND,
} from './aiDialogueTemplates';

/**
 * M4 aiDialogueSettings 管理画面 (docs/04 §3-1 M4、Phase 3.5 admin 第 6 弾)。
 *
 * 旧 `AiDialoguePromptSource` の `hardcoded:*` ロジックは廃止 (docs/04 L114 / §4-1 L254
 * `source: 'firestore'` 固定)。マスター必須化により全 dialogue prompt は本 collection で管理。
 *
 * 各 step は { stepId, prompt, enabled } の単純な struct (shared-types AiDialogueSettings.steps)。
 *
 * docs/105 §6 M4 対話 UI 拡充 (Sprint 5+): 旧 ECW dialogue 互換フィールドの編集 UI を追加。
 * shared-types AiDialogueSettings は `systemBase` / `analysisCommentStyle` / `templateVars` /
 * `stepEnabled` を既に optional で保持しており、本 page で編集できるようにした。
 *
 * ── Admin Wave AW9 (docs/108 ai-dialogue Critical #3 / High / Medium) ───────────────
 * old2 `apps/admin/src/pages/AiDialogueSettingsPage.tsx` を正として以下を移植:
 *   - **Critical #3 / Step0 専用枠**: stepId='step0' を「最初の問いかけ（AI呼び出しなし）」として
 *     特別表示。役割ラベル + 説明「回答は {{USER_INITIAL_THOUGHTS}} に展開、AI は呼ばれない」+
 *     使用 (enabled) チェック。step0 が無ければ 1 クリックで追加するボタンを提供。
 *   - **High #1 / 役割ラベル**: stepId 既知値 (step0..step4) に ECW 標準 4 ステップの意味
 *     (現状認識 / 原因分析 / 処方箋 / 効能) をヒント表示。
 *   - **High #2 / テンプレから新規作成**: ECW casual/standard/walkable + 防災標準 の完成プリセット
 *     (プロンプト本文込み、`aiDialogueTemplates.ts`) をワンクリック生成 (old2 createPresetFromTemplate)。
 *   - **High #3 / 使用地区の可視化**: districts の `aiDialogue.settingsId` 参照を集計し、一覧に
 *     「(N地区)」バッジ + 編集に「使用地区」パネル + アーカイブ前の使用中ガード。
 *   - **Medium**: 変数凡例 (利用可能テンプレ変数 10 種を <details> ヘルプ) / notes 運用メモ textarea
 *     (shared-types に notes?:string を additive 追加) / enabled トグル (保存時 hardcode 廃止) /
 *     version 表示。
 *
 * v2 良差分は維持: steps[] dynamic add/remove/reorder + validation + MasterHistoryView +
 * archive + RowExport/import + 日本語 UI。
 *
 * ── 「AI に渡す投稿データ」セクション (2026-08-31、shared-types `factsConfig`) ────────────
 * 対話の {{FACTS}} に載せる投稿データ (投稿者が入力した項目 / AI 生成タイトル / AI 分類コード /
 * 分類コードの対応表 / AI の所見テキスト) を「通常時」「大量選択時」の 2 組で設定できるようにした。
 * 組立自体はユーザーアプリ (`formatPostsAsFacts`) 側で行い、callable は本設定を読まない。
 * 未設定なら現行の既定 (所見テキスト以外すべて) で動くため、既存の対話設定は影響を受けない。
 */

const COLLECTION_NAME = 'aiDialogueSettings';

type Step = AiDialogueSettings['steps'][number];

/**
 * 「AI に渡す投稿データ」1 組分の編集 draft (通常時 / 大量選択時 で同じ形)。
 * shared-types `AiDialogueFactsFieldSet` の UI 表現 (postFields の 3 択 + テキスト入力)。
 */
interface FactsFieldSetDraft {
  /** 投稿者が入力した項目: 全部 / 指定キーのみ / 渡さない。 */
  postFieldsMode: 'all' | 'selected' | 'none';
  /** postFieldsMode='selected' のときの項目キー (カンマ区切り)。 */
  postFieldKeysText: string;
  aiGeneratedTitle: boolean;
  categoryCodes: boolean;
  categoryMaster: boolean;
  /** AI の所見テキストの項目キー (チェックボックスで選択中のもの)。 */
  aiTextFields: string[];
  /** 候補にないキーの自由入力 (カンマ区切り、保存時に aiTextFields へマージ)。 */
  aiTextFieldsExtraText: string;
}

interface FormDraft {
  id: string;
  displayName: string;
  mode: DistrictMode;
  steps: Step[];
  /** 共通前提文 (systemBase)。空文字で「未設定」を表す (保存時 undefined に変換)。 */
  systemBase: string;
  /** 口調スタイル (analysisCommentStyle、`{{COMMENT_STYLE}}` 展開)。 */
  analysisCommentStyle: string;
  /** 差し込み変数一覧 (templateVars)。UI ではカンマ区切り文字列で編集。 */
  templateVarsText: string;
  /** 運用メモ (notes、AW9 Medium)。空文字で省略。 */
  notes: string;
  /** 有効化フラグ (enabled、AW9 Medium — 旧 hardcode:true を編集可に)。 */
  enabled: boolean;
  /** 既存 doc の version (表示用、新規は 0)。 */
  version: number;
  /** AI に渡す投稿データを個別に設定するか (false = 未設定 = 既定どおり渡す)。 */
  factsConfigEnabled: boolean;
  /** 編集前の doc が factsConfig を持っていたか (持っていなければ保存 payload に含めない)。 */
  hadFactsConfig: boolean;
  /** 通常時に渡す項目。 */
  facts: FactsFieldSetDraft;
  /** 大量選択時に項目を減らすか。 */
  largeSelectionEnabled: boolean;
  /** 何件を超えたら減らすか (入力途中を許すため文字列で保持)。 */
  largeThresholdText: string;
  /** 大量選択時に渡す項目。 */
  largeFacts: FactsFieldSetDraft;
}

/** 通常時の既定 draft = 現行の既定挙動 (投稿者入力項目すべて + AI 生成タイトル + 分類コード + 対応表)。 */
const DEFAULT_FACTS_DRAFT: FactsFieldSetDraft = {
  postFieldsMode: 'all',
  postFieldKeysText: '',
  aiGeneratedTitle: true,
  categoryCodes: true,
  categoryMaster: true,
  aiTextFields: [],
  aiTextFieldsExtraText: '',
};

/** 大量選択時の既定 draft = 「AI が生成したタイトルだけ」(容量を最小に絞る初期案)。 */
const DEFAULT_LARGE_FACTS_DRAFT: FactsFieldSetDraft = {
  postFieldsMode: 'none',
  postFieldKeysText: '',
  aiGeneratedTitle: true,
  categoryCodes: false,
  categoryMaster: false,
  aiTextFields: [],
  aiTextFieldsExtraText: '',
};

/** 大量選択時の閾値の既定値 (件)。 */
const DEFAULT_LARGE_THRESHOLD = '30';

const EMPTY_DRAFT: FormDraft = {
  id: '',
  displayName: '',
  mode: 'ecw',
  steps: [],
  systemBase: '',
  analysisCommentStyle: '',
  templateVarsText: '',
  notes: '',
  enabled: true,
  version: 0,
  factsConfigEnabled: false,
  hadFactsConfig: false,
  facts: DEFAULT_FACTS_DRAFT,
  largeSelectionEnabled: false,
  largeThresholdText: DEFAULT_LARGE_THRESHOLD,
  largeFacts: DEFAULT_LARGE_FACTS_DRAFT,
};

/** カンマ / 読点 / 改行区切りの入力を trim + 空要素除去した配列にする。 */
function splitKeys(text: string): string[] {
  return text
    .split(/[,、\n]/)
    .map((v) => v.trim())
    .filter((v) => v.length > 0);
}

/**
 * 「AI の所見テキスト」の候補 (モード別)。
 * 内部キーは管理者が認識して選ぶ値なので、日本語の説明を主にしつつキーを併記する。
 * 候補にないキー (fields[] 駆動で地区ごとに変わる項目) は自由入力で追加できる。
 */
const COMMON_AI_TEXT_CANDIDATES: { key: string; label: string }[] = [
  { key: 'description', label: 'AI の状況説明' },
  { key: 'professional_insight', label: 'AI の所見（専門家視点の気づき）' },
  { key: 'reasoning', label: 'AI の判断根拠' },
];

const ECW_AI_TEXT_CANDIDATES: { key: string; label: string }[] = [
  ...COMMON_AI_TEXT_CANDIDATES,
  { key: 'emotionText', label: 'AI が読み取った感情' },
  { key: 'emotionReason', label: 'AI の感情判断の理由' },
  { key: 'categoryReason', label: 'AI の分類理由' },
];

function aiTextCandidates(mode: DistrictMode): { key: string; label: string }[] {
  return mode === 'ecw' ? ECW_AI_TEXT_CANDIDATES : COMMON_AI_TEXT_CANDIDATES;
}

/** draft → shared-types (保存用)。 */
function toFactsFieldSet(d: FactsFieldSetDraft): AiDialogueFactsFieldSet {
  const postFields =
    d.postFieldsMode === 'all'
      ? true
      : d.postFieldsMode === 'none'
        ? false
        : splitKeys(d.postFieldKeysText);
  // チェック済 + 自由入力 を結合し、空白のみの要素は除去 + 重複排除。
  const aiTextFields = [...new Set([...d.aiTextFields, ...splitKeys(d.aiTextFieldsExtraText)])];
  return {
    postFields,
    aiGeneratedTitle: d.aiGeneratedTitle,
    categoryCodes: d.categoryCodes,
    categoryMaster: d.categoryMaster,
    aiTextFields,
  };
}

/** shared-types → draft (編集 pre-fill 用)。未設定 key は既定 (現行挙動) で埋める。 */
function toFactsDraft(
  src: AiDialogueFactsFieldSet | null | undefined,
  fallback: FactsFieldSetDraft,
): FactsFieldSetDraft {
  if (!src) return fallback;
  const pf = src.postFields;
  return {
    postFieldsMode: pf === false ? 'none' : Array.isArray(pf) ? 'selected' : 'all',
    postFieldKeysText: Array.isArray(pf) ? pf.join(', ') : '',
    aiGeneratedTitle: src.aiGeneratedTitle ?? true,
    categoryCodes: src.categoryCodes ?? true,
    categoryMaster: src.categoryMaster ?? true,
    aiTextFields: [...(src.aiTextFields ?? [])],
    aiTextFieldsExtraText: '',
  };
}

const EMPTY_STEP: Step = {
  stepId: '',
  prompt: '',
  enabled: true,
};

const INPUT_CLASS =
  'w-full px-2 py-1 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-400';

/**
 * 「AI に渡す投稿データ」1 組分の編集 UI (通常時 / 大量選択時 で共用)。
 *
 * `title` は aria-label の接頭辞にも使い、2 組のコントロールを取り違えないようにする。
 */
function FactsFieldSetEditor({
  title,
  idPrefix,
  mode,
  draft,
  onChange,
}: {
  title: string;
  idPrefix: string;
  mode: DistrictMode;
  draft: FactsFieldSetDraft;
  onChange: (next: FactsFieldSetDraft) => void;
}) {
  const candidates = aiTextCandidates(mode);
  // 候補にないキーがチェック済 (自由入力で追加済 / モード変更後) の場合も必ず画面に出す。
  const extraChecked = draft.aiTextFields.filter((k) => !candidates.some((c) => c.key === k));
  const rows = [...candidates, ...extraChecked.map((k) => ({ key: k, label: k }))];

  function toggleAiTextField(key: string, checked: boolean) {
    const next = checked
      ? [...new Set([...draft.aiTextFields, key])]
      : draft.aiTextFields.filter((k) => k !== key);
    onChange({ ...draft, aiTextFields: next });
  }

  return (
    <div
      className="p-3 bg-white rounded border border-slate-200 grid grid-cols-1 gap-2"
      data-testid={`facts-fieldset-${idPrefix}`}
    >
      <p className="text-xs font-bold text-slate-700">{title}</p>

      <div>
        <span className="block text-xs text-slate-600 mb-1">投稿者が入力した項目</span>
        <div className="flex flex-wrap gap-3">
          {(
            [
              ['all', 'すべて渡す'],
              ['selected', '指定した項目だけ渡す'],
              ['none', '渡さない'],
            ] as const
          ).map(([value, label]) => (
            <label key={value} className="flex items-center gap-1 text-xs text-slate-700">
              <input
                type="radio"
                name={`${idPrefix}-post-fields-mode`}
                checked={draft.postFieldsMode === value}
                onChange={() => onChange({ ...draft, postFieldsMode: value })}
                aria-label={`${title}: 投稿者が入力した項目 ${label}`}
              />
              {label}
            </label>
          ))}
        </div>
        {draft.postFieldsMode === 'selected' && (
          <input
            type="text"
            value={draft.postFieldKeysText}
            onChange={(e) => onChange({ ...draft, postFieldKeysText: e.target.value })}
            className={`${INPUT_CLASS} mt-1`}
            placeholder="渡す項目のキーをカンマ区切りで (例: title, comment)"
            aria-label={`${title}: 渡す投稿項目のキー`}
          />
        )}
      </div>

      <div className="flex flex-wrap gap-4">
        <label className="flex items-center gap-1 text-xs text-slate-700">
          <input
            type="checkbox"
            checked={draft.aiGeneratedTitle}
            onChange={(e) => onChange({ ...draft, aiGeneratedTitle: e.target.checked })}
            aria-label={`${title}: AI が生成したタイトル`}
          />
          AI が生成したタイトル
        </label>
        <label className="flex items-center gap-1 text-xs text-slate-700">
          <input
            type="checkbox"
            checked={draft.categoryCodes}
            onChange={(e) => onChange({ ...draft, categoryCodes: e.target.checked })}
            aria-label={`${title}: AI の分類コード`}
          />
          AI の分類コード
        </label>
        <label className="flex items-center gap-1 text-xs text-slate-700">
          <input
            type="checkbox"
            checked={draft.categoryMaster}
            onChange={(e) => onChange({ ...draft, categoryMaster: e.target.checked })}
            aria-label={`${title}: 分類コードの対応表`}
          />
          分類コードの対応表
        </label>
      </div>

      <div>
        <span className="block text-xs text-slate-600 mb-1">
          AI の所見テキスト（既定では渡していません）
        </span>
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {rows.map((c) => (
            <label key={c.key} className="flex items-center gap-1 text-xs text-slate-700">
              <input
                type="checkbox"
                checked={draft.aiTextFields.includes(c.key)}
                onChange={(e) => toggleAiTextField(c.key, e.target.checked)}
                aria-label={`${title}: ${c.label}`}
              />
              {c.label}
              <span className="text-slate-400">（{c.key}）</span>
            </label>
          ))}
        </div>
        <input
          type="text"
          value={draft.aiTextFieldsExtraText}
          onChange={(e) => onChange({ ...draft, aiTextFieldsExtraText: e.target.value })}
          className={`${INPUT_CLASS} mt-1`}
          placeholder="候補にない項目はここにカンマ区切りで追加 (保存すると上のチェック欄に並びます)"
          aria-label={`${title}: その他の所見テキストの項目キー`}
        />
      </div>
    </div>
  );
}

function validateAiDialogueSettings(raw: unknown): (AiDialogueSettings & { id: string }) | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.id !== 'string' || !r.id.trim()) return null;
  if (typeof r.displayName !== 'string') return null;
  if (typeof r.mode !== 'string') return null;
  return r as unknown as AiDialogueSettings & { id: string };
}

/** stepId 既知値の役割ラベル (High #1)。未知 stepId は null。 */
function roleHintFor(stepId: string): string | null {
  return STEP_ROLE_LABELS[stepId.trim()] ?? null;
}

/**
 * 各 dialogue プリセットの使用地区マップ (settingsId → 地区名[]) を subscribe する hook
 * (High #3、old2 loadDistrictDialogueMap 相当)。
 *
 * districts-admin の `useDistricts` を使わず本 feature 内で firestore を直接 subscribe する理由:
 * districts-admin barrel は MapLibre 依存 panel を re-export しており、それ経由で import すると
 * masters 配下に重量級依存を引き込むため。ここでは `aiDialogue.settingsId` と `name` のみ参照する
 * 軽量 read に留める。read 認可は firestore.rules の master role で担保。
 */
function useDialogueDistrictUsage(): Record<string, string[]> {
  const [usageMap, setUsageMap] = useState<Record<string, string[]>>({});
  useEffect(() => {
    const ref = collection(db, 'districts');
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const map: Record<string, string[]> = {};
        for (const docSnap of snap.docs) {
          const data = docSnap.data() as DocumentData;
          const aiDialogue = data.aiDialogue as { settingsId?: unknown } | undefined;
          const settingsId =
            aiDialogue && typeof aiDialogue.settingsId === 'string' && aiDialogue.settingsId
              ? aiDialogue.settingsId
              : null;
          if (settingsId) {
            const name = typeof data.name === 'string' && data.name ? data.name : docSnap.id;
            (map[settingsId] ??= []).push(name);
          }
        }
        setUsageMap(map);
      },
      () => setUsageMap({}),
    );
    return () => unsub();
  }, []);
  return usageMap;
}

export function AiDialogueSettingsPage() {
  const state = useAiDialogueSettings();
  const districtUsageMap = useDialogueDistrictUsage();
  const { toast } = useToast();
  const [editing, setEditing] = useState<FormDraft | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [historyTarget, setHistoryTarget] = useState<{ id: string; name: string } | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<{ id: string; name: string } | null>(null);

  async function handleImport(importedItems: (AiDialogueSettings & { id: string })[]) {
    for (const it of importedItems) {
      const existing =
        state.status === 'ready' ? state.items.find((x) => x.id === it.id) : undefined;
      // Sprint BG-MasterIO follow-up — system field を除去
      const { id, ...rest } = stripSystemFields(it);
      await upsertMaster<AiDialogueSettings>({
        collectionName: COLLECTION_NAME,
        id: id as string,
        uid: 'admin-stub',
        isNew: !existing,
        data: rest as Omit<
          AiDialogueSettings,
          'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy'
        >,
      });
    }
  }

  function handleNew() {
    setEditing({ ...EMPTY_DRAFT, steps: [] });
    setIsNew(true);
  }

  /** テンプレから新規作成 (High #2、old2 createPresetFromTemplate 相当)。 */
  function handleNewFromTemplate(key: string) {
    const tpl = DIALOGUE_SCENARIO_TEMPLATES.find((t) => t.key === key);
    if (!tpl) return;
    const exists = state.status === 'ready' && state.items.some((x) => x.id === tpl.id);
    setEditing({
      ...EMPTY_DRAFT,
      // 既存 id と衝突する場合はサフィックスを付けて新規衝突を避ける (保存前に admin が調整可能)。
      id: exists ? `${tpl.id}-copy` : tpl.id,
      displayName: tpl.displayName,
      mode: tpl.mode,
      steps: tpl.steps.map((s) => ({ ...s })),
      systemBase: tpl.systemBase,
      analysisCommentStyle: tpl.analysisCommentStyle,
      templateVarsText: tpl.templateVars.join(', '),
      enabled: true,
      version: 0,
    });
    setIsNew(true);
    toast(
      'success',
      `テンプレ「${tpl.label}」を読み込みました。ID / 文言を確認して保存してください。`,
    );
  }

  function handleEdit(item: AiDialogueSettings & { id: string }) {
    const facts = item.factsConfig ?? null;
    const large = facts?.largeSelection ?? null;
    setEditing({
      id: item.id,
      displayName: item.displayName,
      mode: item.mode,
      steps: item.steps.map((s) => ({ ...s })),
      systemBase: item.systemBase ?? '',
      analysisCommentStyle: item.analysisCommentStyle ?? '',
      templateVarsText: (item.templateVars ?? []).join(', '),
      notes: item.notes ?? '',
      enabled: item.enabled ?? true,
      version: item.version ?? 0,
      factsConfigEnabled: facts !== null,
      hadFactsConfig: facts !== null,
      facts: toFactsDraft(facts, DEFAULT_FACTS_DRAFT),
      largeSelectionEnabled: large !== null,
      largeThresholdText: large ? String(large.thresholdPosts) : DEFAULT_LARGE_THRESHOLD,
      largeFacts: toFactsDraft(large, DEFAULT_LARGE_FACTS_DRAFT),
    });
    setIsNew(false);
  }

  function addStep() {
    if (!editing) return;
    setEditing({ ...editing, steps: [...editing.steps, { ...EMPTY_STEP }] });
  }

  /** Step 0 (最初の問いかけ・AI 呼び出しなし) を先頭に追加 (Critical #3)。 */
  function addStep0() {
    if (!editing) return;
    if (editing.steps.some((s) => s.stepId.trim() === STEP0_ID)) return;
    setEditing({
      ...editing,
      steps: [{ stepId: STEP0_ID, prompt: '', enabled: true }, ...editing.steps],
    });
  }

  function updateStep(index: number, patch: Partial<Step>) {
    if (!editing) return;
    const target = editing.steps[index];
    if (!target) return;
    const next = [...editing.steps];
    next[index] = { ...target, ...patch };
    setEditing({ ...editing, steps: next });
  }

  function removeStep(index: number) {
    if (!editing) return;
    setEditing({ ...editing, steps: editing.steps.filter((_, i) => i !== index) });
  }

  function moveStep(index: number, dir: -1 | 1) {
    if (!editing) return;
    const target = index + dir;
    if (target < 0 || target >= editing.steps.length) return;
    const next = [...editing.steps];
    const a = next[index];
    const b = next[target];
    if (!a || !b) return;
    next[index] = b;
    next[target] = a;
    setEditing({ ...editing, steps: next });
  }

  async function handleSave() {
    if (!editing) return;
    if (!editing.displayName.trim()) {
      toast('error', 'displayName は必須です');
      return;
    }
    if (isNew && !editing.id.trim()) {
      toast('error', '新規時は id (kebab-case) を指定してください');
      return;
    }
    // steps[] バリデーション: stepId / prompt は必須、stepId は重複禁止
    const cleanedSteps: Step[] = [];
    const seenIds = new Set<string>();
    let stepIndex = 0;
    for (const s of editing.steps) {
      stepIndex++;
      const stepId = s.stepId.trim();
      const prompt = s.prompt.trim();
      if (!stepId || !prompt) {
        toast('error', `step ${stepIndex}: stepId と prompt は必須です`);
        return;
      }
      if (seenIds.has(stepId)) {
        toast('error', `step ${stepIndex}: stepId "${stepId}" が重複しています`);
        return;
      }
      seenIds.add(stepId);
      cleanedSteps.push({ stepId, prompt, enabled: s.enabled });
    }
    // 旧 ECW dialogue 互換フィールド: 空欄は payload から省略 (undefined)。
    const systemBase = editing.systemBase.trim();
    const analysisCommentStyle = editing.analysisCommentStyle.trim();
    const notes = editing.notes.trim();
    const templateVars = editing.templateVarsText
      .split(',')
      .map((v) => v.trim())
      .filter((v) => v.length > 0);

    // AI に渡す投稿データ (factsConfig)。個別設定 OFF なら未設定扱い (null)。
    // 元々未設定 かつ OFF のままなら payload に含めない (既存 doc をそのまま維持)。
    let factsConfig: AiDialogueFactsConfig | null = null;
    if (editing.factsConfigEnabled) {
      let largeSelection: (AiDialogueFactsFieldSet & { thresholdPosts: number }) | null = null;
      if (editing.largeSelectionEnabled) {
        const threshold = Number(editing.largeThresholdText.trim());
        if (!Number.isInteger(threshold) || threshold < 1) {
          toast('error', '大量選択時の件数は 1 以上の整数で入力してください');
          return;
        }
        largeSelection = { ...toFactsFieldSet(editing.largeFacts), thresholdPosts: threshold };
      }
      // merge 書込では入れ子も再帰マージされるため、OFF の場合も明示的に null を書いて消す。
      factsConfig = { ...toFactsFieldSet(editing.facts), largeSelection };
    }
    const includeFactsConfig = editing.factsConfigEnabled || editing.hadFactsConfig;

    setSaving(true);
    try {
      await upsertMaster<AiDialogueSettings>({
        collectionName: COLLECTION_NAME,
        id: editing.id || undefined,
        uid: 'admin-stub', // Phase 5 で実 master role uid に置換
        isNew,
        data: {
          mode: editing.mode,
          displayName: editing.displayName.trim(),
          steps: cleanedSteps,
          ...(systemBase ? { systemBase } : {}),
          ...(analysisCommentStyle ? { analysisCommentStyle } : {}),
          ...(templateVars.length > 0 ? { templateVars } : {}),
          ...(notes ? { notes } : {}),
          ...(includeFactsConfig ? { factsConfig } : {}),
          // AW9 Medium: enabled は編集可 (旧 hardcode:true を廃止)。
          enabled: editing.enabled,
          version: 1,
          schemaVersion: 1,
          archivedAt: null,
        },
      });
      toast('success', isNew ? '新規作成しました' : '更新しました');
      setEditing(null);
      setIsNew(false);
    } catch (e) {
      toast('error', `保存失敗: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  /** アーカイブ実行 (使用中ガードは UI の confirm で事前に判定済)。 */
  async function performArchive(id: string) {
    try {
      await archiveMaster(COLLECTION_NAME, id, 'admin-stub'); // Phase 5 で実 master role uid に置換
      toast('success', 'アーカイブしました');
    } catch (e) {
      toast('error', `アーカイブ失敗: ${(e as Error).message}`);
    } finally {
      setArchiveTarget(null);
    }
  }

  /** アーカイブ要求: 使用中なら拒否、未使用なら confirm dialog (High #3 削除ガード)。 */
  function requestArchive(id: string, name: string) {
    const usedBy = districtUsageMap[id] ?? [];
    if (usedBy.length > 0) {
      toast('error', `以下の地区で使用中のためアーカイブできません: ${usedBy.join(' / ')}`);
      return;
    }
    setArchiveTarget({ id, name });
  }

  const editingUsageDistricts = editing ? (districtUsageMap[editing.id] ?? []) : [];

  return (
    <div>
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">AI 対話設定マスター</h1>
        <p className="text-sm text-slate-500 mt-1">
          ECW 対話プロンプト (内部 ID: aiDialogueSettings、docs/04 §3-1 M4)。旧 hardcoded:* 廃止 /
          firestore 一本化。
        </p>
      </header>

      <div className="mb-2 flex flex-wrap gap-2 items-center">
        <Button variant="primary" size="md" onClick={handleNew} disabled={editing !== null}>
          ＋ 新規作成
        </Button>
        <MasterImportExportButtons<AiDialogueSettings & { id: string }>
          collectionName={COLLECTION_NAME}
          items={state.status === 'ready' ? state.items : []}
          validator={validateAiDialogueSettings}
          onImport={handleImport}
          disabled={editing !== null}
          expectedMode={['ecw', 'bousai', 'walker', 'kids-bousai']}
        />
      </div>

      {/* High #2: テンプレから新規作成ボタン群 (old2 createPresetFromTemplate) */}
      <div className="mb-4 flex flex-wrap gap-2 items-center">
        <span className="text-xs text-slate-500">テンプレから新規作成:</span>
        {DIALOGUE_SCENARIO_TEMPLATES.map((tpl) => (
          <Button
            key={tpl.key}
            variant="secondary"
            size="sm"
            onClick={() => handleNewFromTemplate(tpl.key)}
            disabled={editing !== null}
          >
            ＋ {tpl.label}
          </Button>
        ))}
      </div>

      {state.status === 'loading' && <p className="text-slate-500">読込中…</p>}
      {state.status === 'error' && (
        <p className="text-danger-600">読込エラー: {state.error.message}</p>
      )}
      {state.status === 'ready' && (
        <>
          <p className="text-xs text-slate-500 mb-3">
            件数: {state.items.length} (アーカイブ済除外)
          </p>
          <ul className="grid grid-cols-1 gap-2 mb-6">
            {state.items.map((item) => {
              const enabledCount = item.steps.filter((s) => s.enabled).length;
              const usageCount = (districtUsageMap[item.id] ?? []).length;
              return (
                <li key={item.id}>
                  <Card padding="md" className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <strong className="text-slate-900">{item.displayName}</strong>
                      {!item.enabled && (
                        <span className="ml-2 text-xs text-warning-700 font-semibold">[無効]</span>
                      )}
                      <span className="ml-2 text-xs text-slate-500">id: {item.id}</span>
                      <span className="ml-2 text-xs text-slate-500">mode: {item.mode}</span>
                      <span className="ml-2 text-xs text-slate-500">
                        steps: {enabledCount}/{item.steps.length} 有効
                      </span>
                      {/* High #3: 使用地区バッジ */}
                      {usageCount > 0 && (
                        <span
                          className="ml-2 text-xs text-primary-700 font-semibold"
                          data-testid={`usage-badge-${item.id}`}
                        >
                          ({usageCount}地区)
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <RowExportButton collectionName={COLLECTION_NAME} item={item} />
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleEdit(item)}
                        aria-label={`${item.displayName} を編集`}
                      >
                        編集
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setHistoryTarget({ id: item.id, name: item.displayName })}
                        aria-label={`${item.displayName} の履歴を表示`}
                      >
                        履歴
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => requestArchive(item.id, item.displayName)}
                        aria-label={`${item.displayName} をアーカイブ`}
                      >
                        アーカイブ
                      </Button>
                    </div>
                  </Card>
                </li>
              );
            })}
          </ul>
        </>
      )}

      {historyTarget && (
        <MasterHistoryView
          collectionName={COLLECTION_NAME}
          masterId={historyTarget.id}
          masterDisplayName={historyTarget.name}
          onClose={() => setHistoryTarget(null)}
        />
      )}

      <ConfirmDialog
        open={archiveTarget !== null}
        intent="danger"
        title="アーカイブの確認"
        message={
          archiveTarget
            ? `「${archiveTarget.name}」をアーカイブしますか? (物理削除ではなく archivedAt 設定)`
            : ''
        }
        confirmLabel="アーカイブ"
        onConfirm={() => archiveTarget && void performArchive(archiveTarget.id)}
        onCancel={() => setArchiveTarget(null)}
      />

      {editing && (
        <Card padding="lg" className="border-primary-300">
          <h2 className="text-lg font-bold mb-3">{isNew ? '新規作成' : '編集'}</h2>
          <div className="grid grid-cols-1 gap-3">
            {isNew && (
              <div>
                <label className="block text-xs text-slate-600 mb-1" htmlFor="m4-id">
                  ドキュメント ID (id) — kebab-case、保存後変更不可{' '}
                  <span className="text-danger-600">*</span>
                </label>
                <input
                  id="m4-id"
                  type="text"
                  value={editing.id}
                  onChange={(e) => setEditing({ ...editing, id: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400"
                  placeholder="例: ecw-casual"
                />
              </div>
            )}
            {!isNew && (
              <p className="text-xs text-slate-500">
                <code className="px-2 py-0.5 bg-slate-100 rounded">{editing.id}</code>
                <span className="ml-2">ver {editing.version}</span>
              </p>
            )}

            {/* High #3: 使用地区パネル */}
            <div
              className="p-3 bg-slate-50 rounded border border-slate-200"
              data-testid="usage-districts-panel"
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-semibold text-slate-600">使用地区</span>
                <span className="text-xs text-slate-400">({editingUsageDistricts.length}件)</span>
              </div>
              {editingUsageDistricts.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {editingUsageDistricts.map((d) => (
                    <span
                      key={d}
                      className="px-2 py-0.5 bg-primary-100 text-primary-700 text-xs rounded-full"
                    >
                      {d}
                    </span>
                  ))}
                </div>
              ) : (
                <span className="text-xs text-warning-700">どの地区でも使用されていません</span>
              )}
              <p className="text-[11px] text-slate-400 mt-1">
                地区との紐付けは「地区詳細」の「AI
                対話設定」セクションから行います。使用中のプリセットはアーカイブできません。
              </p>
            </div>

            <div>
              <label className="block text-xs text-slate-600 mb-1" htmlFor="m4-name">
                表示名称 (displayName) <span className="text-danger-600">*</span>
              </label>
              <input
                id="m4-name"
                type="text"
                value={editing.displayName}
                onChange={(e) => setEditing({ ...editing, displayName: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-600 mb-1" htmlFor="m4-mode">
                モード (mode)
              </label>
              {/* MD-3 モードドリブン化: 選択肢は ModeProfile (M10) 登記簿から動的生成 (未登録の現在値も表示)。 */}
              <ModeSelectField
                id="m4-mode"
                value={editing.mode}
                onChange={(mode) => setEditing({ ...editing, mode })}
              />
            </div>

            {/* AW9 Medium: enabled トグル (旧 hardcode:true を編集可に) */}
            <div>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={editing.enabled}
                  onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })}
                  aria-label="有効化 (enabled)"
                />
                このプリセットを使用可能にする (enabled)
              </label>
            </div>

            {/* AW9 Medium: 変数凡例 (利用可能テンプレート変数 10 種) */}
            <details className="p-3 bg-slate-50 rounded border border-slate-200 text-xs">
              <summary className="font-semibold text-slate-600 cursor-pointer">
                利用可能なテンプレート変数 (10 種)
              </summary>
              <p className="mt-2 text-slate-600">
                プロンプト本文や口調スタイルに下記の {'{{'}変数名{'}}'} を埋め込むと、Cloud Function
                側で展開されます。
              </p>
              <ul className="mt-2 grid grid-cols-1 gap-1">
                {TEMPLATE_VAR_LEGEND.map((v) => (
                  <li key={v.name} className="flex gap-2">
                    <code className="px-1.5 py-0.5 bg-white border border-slate-200 rounded text-slate-700 shrink-0">
                      {`{{${v.name}}}`}
                    </code>
                    <span className="text-slate-500">{v.desc}</span>
                  </li>
                ))}
              </ul>
            </details>

            {/* 旧 ECW dialogue 互換フィールド (docs/105 §6 M4 対話 UI 拡充) */}
            <div className="border-t border-slate-200 pt-3 mt-2 grid grid-cols-1 gap-3">
              <div>
                <label className="block text-xs text-slate-600 mb-1" htmlFor="m4-system-base">
                  共通前提文 (systemBase) — 全ステップ共通のシステムプロンプト
                </label>
                <textarea
                  id="m4-system-base"
                  value={editing.systemBase}
                  onChange={(e) => setEditing({ ...editing, systemBase: e.target.value })}
                  rows={6}
                  className="w-full px-3 py-2 text-xs border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400 resize-y font-mono"
                  placeholder="例: あなたは都市のウェルビーイングを実現するためのワークショップのファシリテーターAIです。…"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-600 mb-1" htmlFor="m4-comment-style">
                  口調スタイル (analysisCommentStyle) — {'{{COMMENT_STYLE}}'} に展開される短文
                </label>
                <textarea
                  id="m4-comment-style"
                  value={editing.analysisCommentStyle}
                  onChange={(e) => setEditing({ ...editing, analysisCommentStyle: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 text-xs border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400 resize-y"
                  placeholder="例: 最大100文字程度。前置きは不要です。"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-600 mb-1" htmlFor="m4-template-vars">
                  差し込み変数一覧 (templateVars) — カンマ区切り、{'{{VAR}}'} の VAR 名のみ
                  (ドキュメント用)
                </label>
                <input
                  id="m4-template-vars"
                  type="text"
                  value={editing.templateVarsText}
                  onChange={(e) => setEditing({ ...editing, templateVarsText: e.target.value })}
                  className="w-full px-3 py-2 text-xs border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400"
                  placeholder="例: EMOTION, CATEGORY, FACTS, QUESTIONS, COMMENT_STYLE"
                />
              </div>
            </div>

            {/* AI に渡す投稿データ (factsConfig、2026-08-31) */}
            <div
              className="border-t border-slate-200 pt-3 mt-2 grid grid-cols-1 gap-2"
              data-testid="facts-config-section"
            >
              <p className="text-sm font-bold text-slate-700">AI に渡す投稿データ</p>
              <p className="text-[11px] text-slate-500">
                対話で AI に読ませる、選択された投稿の内容です。設定しない場合は
                <strong>
                  「投稿者が入力した項目すべて ＋ AI が生成したタイトル ＋ AI の分類コード ＋
                  分類コードの対応表」
                </strong>
                を渡します（AI の所見テキストは渡しません）。
              </p>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={editing.factsConfigEnabled}
                  onChange={(e) => setEditing({ ...editing, factsConfigEnabled: e.target.checked })}
                  aria-label="AI に渡す投稿データを個別に設定する"
                />
                AI に渡す投稿データを個別に設定する
              </label>

              {editing.factsConfigEnabled && (
                <div className="grid grid-cols-1 gap-3 p-3 bg-slate-50 rounded border border-slate-200">
                  <FactsFieldSetEditor
                    title="通常時に渡す項目"
                    idPrefix="facts-normal"
                    mode={editing.mode}
                    draft={editing.facts}
                    onChange={(facts) => setEditing({ ...editing, facts })}
                  />

                  <div className="grid grid-cols-1 gap-2">
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={editing.largeSelectionEnabled}
                        onChange={(e) =>
                          setEditing({ ...editing, largeSelectionEnabled: e.target.checked })
                        }
                        aria-label="投稿がたくさん選ばれたときは渡す項目を減らす"
                      />
                      投稿がたくさん選ばれたときは渡す項目を減らす
                    </label>
                    <p className="text-[11px] text-slate-500">
                      多数の投稿が選ばれると AI に渡す文章が長くなりすぎるため、件数が多いときだけ
                      別の（少ない）項目に切り替えられます。切り替わったときは「主要な項目のみ掲載」
                      の注記を AI に添えるので、情報が少ないと誤解されません。
                    </p>
                    {editing.largeSelectionEnabled && (
                      <div className="grid grid-cols-1 gap-2">
                        <div className="flex items-center gap-2">
                          <label className="text-xs text-slate-600" htmlFor="m4-facts-threshold">
                            何件を超えたら減らすか
                          </label>
                          <input
                            id="m4-facts-threshold"
                            type="number"
                            min={1}
                            step={1}
                            value={editing.largeThresholdText}
                            onChange={(e) =>
                              setEditing({ ...editing, largeThresholdText: e.target.value })
                            }
                            className="w-24 px-2 py-1 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-400"
                          />
                          <span className="text-xs text-slate-600">件</span>
                        </div>
                        <FactsFieldSetEditor
                          title="大量選択時に渡す項目"
                          idPrefix="facts-large"
                          mode={editing.mode}
                          draft={editing.largeFacts}
                          onChange={(largeFacts) => setEditing({ ...editing, largeFacts })}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* steps[] 編集 */}
            <div className="border-t border-slate-200 pt-3 mt-2">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <label className="block text-sm font-bold text-slate-700">
                  対話ステップ (steps[]、{editing.steps.length} 件)
                </label>
                <div className="flex gap-2">
                  {!editing.steps.some((s) => s.stepId.trim() === STEP0_ID) && (
                    <Button variant="secondary" size="sm" onClick={addStep0}>
                      ＋ Step 0 (最初の問いかけ)
                    </Button>
                  )}
                  <Button variant="secondary" size="sm" onClick={addStep}>
                    ＋ step 追加
                  </Button>
                </div>
              </div>
              {/* Critical #3: step0 の意味と {{USER_INITIAL_THOUGHTS}} 展開を明示するヘルプ。 */}
              <p className="text-[11px] text-slate-500 mb-2">
                stepId に <code>step0</code> を指定すると「最初の問いかけ（AI
                呼び出しなし）」になります。{STEP0_DESCRIPTION}
                stepId <code>step1〜step4</code> は ECW 標準 4 ステップ（現状認識 / 原因分析 /
                処方箋 / 効能）として役割ラベルを表示します。
              </p>
              {editing.steps.length === 0 ? (
                <p className="text-xs text-slate-500">
                  step がありません。「＋ step 追加」または「＋ Step 0」で追加。
                </p>
              ) : (
                <ul className="grid grid-cols-1 gap-3">
                  {editing.steps.map((step, i) => {
                    const trimmedId = step.stepId.trim();
                    const isStep0 = trimmedId === STEP0_ID;
                    const roleHint = roleHintFor(trimmedId);
                    return (
                      <li
                        key={trimmedId || `__draft-${i}`}
                        className={`border rounded-md p-3 ${
                          isStep0
                            ? 'border-primary-300 bg-primary-50'
                            : 'border-slate-200 bg-slate-50'
                        }`}
                      >
                        <div className="grid grid-cols-1 gap-2">
                          {/* High #1 / Critical #3: 役割ラベル行 */}
                          {(roleHint || isStep0) && (
                            <p
                              className={`text-xs font-semibold ${
                                isStep0 ? 'text-primary-700' : 'text-slate-600'
                              }`}
                              data-testid={`role-label-${i}`}
                            >
                              {isStep0 ? 'Step 0 — 最初の問いかけ（AI呼び出しなし）' : roleHint}
                            </p>
                          )}
                          {isStep0 && (
                            <p className="text-[11px] text-primary-700/80">{STEP0_DESCRIPTION}</p>
                          )}
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs text-slate-500 shrink-0">#{i + 1}</span>
                            <input
                              type="text"
                              value={step.stepId}
                              onChange={(e) => updateStep(i, { stepId: e.target.value })}
                              className={`${INPUT_CLASS} flex-1`}
                              placeholder="stepId (例: step0 / step1 / ask-feeling)"
                              aria-label={`ステップ ${i + 1} ステップ ID (stepId)`}
                            />
                            <label className="flex items-center gap-1 text-xs text-slate-700 shrink-0">
                              <input
                                type="checkbox"
                                checked={step.enabled}
                                onChange={(e) => updateStep(i, { enabled: e.target.checked })}
                                className="cursor-pointer"
                                aria-label={
                                  isStep0
                                    ? `ステップ ${i + 1} 使用する (enabled)`
                                    : `ステップ ${i + 1} enabled`
                                }
                              />
                              {isStep0 ? '使用する' : 'enabled'}
                            </label>
                            <div className="flex gap-1 shrink-0">
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => moveStep(i, -1)}
                                disabled={i === 0}
                                aria-label={`step ${i + 1} を上へ`}
                              >
                                ↑
                              </Button>
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => moveStep(i, 1)}
                                disabled={i === editing.steps.length - 1}
                                aria-label={`step ${i + 1} を下へ`}
                              >
                                ↓
                              </Button>
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => removeStep(i)}
                                aria-label={`step ${i + 1} 削除`}
                              >
                                ✕
                              </Button>
                            </div>
                          </div>
                          <textarea
                            value={step.prompt}
                            onChange={(e) => updateStep(i, { prompt: e.target.value })}
                            rows={isStep0 ? 3 : 6}
                            className="w-full px-2 py-1 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-400 resize-y"
                            placeholder={
                              isStep0
                                ? '参加者に最初に表示する固定の問いかけ (回答は {{USER_INITIAL_THOUGHTS}} に展開)'
                                : 'prompt (このステップで AI に渡す日本語プロンプト)'
                            }
                            aria-label={`ステップ ${i + 1} プロンプト (prompt)`}
                          />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/* AW9 Medium: 運用メモ (notes) */}
            <div>
              <label className="block text-xs text-slate-600 mb-1" htmlFor="m4-notes">
                運用メモ (notes、任意) — 運用上の注釈。AI には渡されません。
              </label>
              <textarea
                id="m4-notes"
                value={editing.notes}
                onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 text-xs border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400 resize-y"
              />
            </div>

            <div className="flex justify-end gap-2 mt-2 border-t border-slate-200 pt-3">
              <Button
                variant="secondary"
                size="md"
                onClick={() => {
                  setEditing(null);
                  setIsNew(false);
                }}
                disabled={saving}
              >
                キャンセル
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={() => void handleSave()}
                disabled={saving}
              >
                {saving ? '保存中...' : '保存'}
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
